/**
 * dashboard/index.tsx — Tutor landing.
 *
 * Lives at `/dashboard`. Hard-gated to approved tutors; admins landing here
 * get redirected to `/admin`, students to `/me`, unauthenticated visitors
 * to `/login`.
 */

import type { ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { ClassWall } from '../../components/tutor/ClassWall.js';
import { freshSessionQuery, classesQuery } from '../../lib/api/queries.js';
import type { SessionPrincipal } from '../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'parent') {
      // N-17: parent dashboard ships at /parent. Don't let parents see the
      // tutor dashboard.
      throw redirect({ to: '/parent' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(classesQuery);
    return null;
  },
  component: TutorDashboard,
});

function TutorDashboard(): ReactNode {
  const { queryClient } = Route.useRouteContext();
  const cached = queryClient.getQueryData(classesQuery.queryKey) as
    { ok?: boolean; data?: { classes: unknown[] } } | undefined;
  const classes = (cached?.ok ? (cached.data?.classes ?? []) : []) as never[];

  const session = queryClient.getQueryData(freshSessionQuery.queryKey) as
    { ok?: boolean; data?: { user?: { name: string } } } | undefined;
  const userName = session?.ok ? (session.data?.user?.name ?? 'Tutor') : 'Tutor';

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Dasbor tutor"
        title={`Selamat datang, ${userName.split(' ')[0] ?? 'Tutor'}`}
        description="Kelola kelas, undang siswa, dan pantau progres mereka dari satu tempat."
      />

      <section className="section" aria-labelledby="kelas-anda">
        <h2 className="section-title" id="kelas-anda">
          Kelas Anda
        </h2>
        <ClassWall
          classes={classes}
          emptyTitle="Belum ada kelas"
          emptyDescription="Buat kelas pertama kamu untuk mulai mengelola siswa dan materi."
          emptyHref="/dashboard/classes/new"
          emptyLabel="Buat kelas"
        />
      </section>

      <section className="section" aria-labelledby="perlu-ditindaklanjuti">
        <h2 className="section-title" id="perlu-ditindaklanjuti">
          Yang perlu ditindaklanjuti
        </h2>
        <StatePanel
          variant="empty"
          title="Tidak ada tindakan menunggu"
          description="Pemberitahuan dan permintaan akan muncul di sini ketika ada yang perlu kamu tanggapi."
        />
      </section>

      <section className="section" aria-labelledby="aktivitas-terbaru">
        <h2 className="section-title" id="aktivitas-terbaru">
          Aktivitas terbaru
        </h2>
        <StatePanel
          variant="empty"
          title="Belum ada aktivitas terbaru"
          description="Setelah siswa bergabung dan mulai belajar, progres mereka akan muncul di sini. Lihat semua siswa di menu Siswa di sidebar."
        />
      </section>

      <section className="section" aria-labelledby="buku-panduan">
              <div className="panduan-hub" data-testid="panduan-hub">
                <header className="panduan-hub-header">
                  <p className="section-eyebrow">Buku panduan</p>
                  <h2 id="buku-panduan">Dua panduan wajib untuk tutor Funpaper Coding</h2>
                  <p>
                    Pelajari filosofi di balik game dan sistem lencana supaya kamu bisa menjelaskan
                    ke orang tua dan siswa dengan yakin.
                  </p>
                </header>

                <div className="panduan-cards">
                  <article className="panduan-card" data-testid="panduan-card-leveling">
                    <span className="panduan-card-num" aria-hidden="true">01</span>
                    <h3 className="panduan-card-title">Cara baca leveling game</h3>
                    <p className="panduan-card-desc">
                      Kenapa 200 level di 30 game bukan banyak — itu cara kami mengajarkan
                      satu konsep sampai otomatis.
                    </p>
                    <p className="panduan-toc-label">Yang dibahas di dalam</p>
                    <ol className="panduan-toc">
                      <li className="panduan-toc-item">Apa itu leveling</li>
                      <li className="panduan-toc-item">Cara baca level (awal, tengah, akhir)</li>
                      <li className="panduan-toc-item">Contoh g01-sarapan, 5 level</li>
                      <li className="panduan-toc-item">Kapan lanjut ke level berikutnya</li>
                      <li className="panduan-toc-item">5 tips mengajar</li>
                    </ol>
                    <Link
                      to="/dashboard/leveling-guide"
                      className="button button-primary button-sm panduan-card-cta"
                      data-testid="dashboard-link-leveling-guide"
                    >
                      Baca panduan leveling
                      <span aria-hidden="true"> →</span>
                    </Link>
                  </article>

                  <article className="panduan-card" data-testid="panduan-card-badges">
                    <span className="panduan-card-num" aria-hidden="true">02</span>
                    <h3 className="panduan-card-title">Cara kerja sistem lencana</h3>
                    <p className="panduan-card-desc">
                      14 lencana otomatis dari progres siswa — apa yang muncul, kapan muncul,
                      dan bagaimana memanfaatkannya untuk motivasi.
                    </p>
                    <p className="panduan-toc-label">Yang dibahas di dalam</p>
                    <ol className="panduan-toc">
                      <li className="panduan-toc-item">Apa itu sistem lencana</li>
                      <li className="panduan-toc-item">Cara baca di dashboard siswa</li>
                      <li className="panduan-toc-item">Katalog 14 lencana aktif</li>
                      <li className="panduan-toc-item">Yang bisa tutor lakukan</li>
                      <li className="panduan-toc-item">FAQ singkat</li>
                    </ol>
                    <Link
                      to="/dashboard/panduan-badges"
                      className="button button-primary button-sm panduan-card-cta"
                      data-testid="dashboard-link-panduan-badges"
                    >
                      Baca panduan lencana
                      <span aria-hidden="true"> →</span>
                    </Link>
                  </article>
                </div>
              </div>
            </section>
    </main>
  );
}
